import React, { useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { Folder, Category, CategoryOutlined, Book, EmojiEvents, Collections, ChevronRight } from '@mui/icons-material';

const ParentSelector = ({
  value,
  onChange,
  label = "Parent",
  fullWidth = true,
  allowedTypes = ['category', 'subcategory', 'challenge', 'series', 'audiobook'],
  showQuickTaps = false
}) => {
  // Raw data from Redux
  const rawCategories = useSelector(state => state.categories.all_categories);
  const rawSubcategories = useSelector(state => state.subcategories.subcategories);
  const rawChallenges = useSelector(state => state.challenges.challenges);
  const rawSeries = useSelector(state => state.series.all_series);

  // Memoized data transformations
  const all_categories = useMemo(() => rawCategories || [], [rawCategories]);
  const all_subcategories = useMemo(() =>
    rawSubcategories ? Object.values(rawSubcategories) : [],
    [rawSubcategories]
  );
  const challenges = useMemo(() =>
    rawChallenges ? Object.values(rawChallenges) : [],
    [rawChallenges]
  );
  const series = useMemo(() => {
    if (!rawSeries) return [];
    // Handle both array and object formats
    const seriesArray = Array.isArray(rawSeries) ? rawSeries : Object.values(rawSeries);
    // Ensure each series has the required properties
    return seriesArray.filter(series => series && series.series_id && series.series_title);
  }, [rawSeries]);

  const renderParentOptions = useMemo(() => {
    const options = [];

    // Add None option
    options.push({
      label: "None",
      value: "0",
      group: "None",
      icon: Folder
    });

    // Process categories and their subcategories
    if (allowedTypes.includes('category') || allowedTypes.includes('subcategory')) {
      all_categories
        .filter(category => {
          if (!allowedTypes.includes('category')) return false;
          if (!showQuickTaps && category.category_is_quick_tap) return false;
          if (showQuickTaps && !category.category_is_quick_tap) return false;
          if (category.category_is_audiobook) return false;
          return true;
        })
        .forEach(category => {
          // Add category
          let categoryLabel = category.category_title;
          if (category.category_is_quick_tap) {
            categoryLabel += ' (Quick Tap)';
          }
          if (!category.category_is_active) {
            categoryLabel += ' (Inactive)';
          }

          options.push({
            label: categoryLabel,
            value: String(category.category_id) + "CAT",
            group: "Categories",
            icon: Category,
            disabled: !category.category_is_active
          });

          // Add subcategories under this category
          all_subcategories
            .filter(subcategory => {
              if (!allowedTypes.includes('subcategory')) return false;
              if (subcategory.category_id !== category.category_id) return false;
              if (!showQuickTaps && subcategory.subcategory_is_quick_tap) return false;
              if (showQuickTaps && !subcategory.subcategory_is_quick_tap) return false;
              if (subcategory.subcategory_is_book) return false;
              return true;
            })
            .forEach(subcategory => {
              let label = subcategory.subcategory_title;
              if (subcategory.subcategory_is_quick_tap) {
                label += ' (Quick Tap)';
              }
              if (!subcategory.subcategory_is_active) {
                label += ' (Inactive)';
              }

              options.push({
                label,
                value: String(subcategory.subcategory_id) + "SUB",
                group: "Categories",
                icon: ChevronRight,
                disabled: !subcategory.subcategory_is_active
              });
            });
        });

      // Add orphaned subcategories (those without a parent category)
      // Check if any subcategories are already added to avoid duplicates
      const addedSubcategoryIds = new Set(
        options
          .filter(option => option.value.endsWith('SUB'))
          .map(option => option.value.replace('SUB', ''))
      );

      all_subcategories
        .filter(subcategory => {
          if (!allowedTypes.includes('subcategory')) return false;
          if (!showQuickTaps && subcategory.subcategory_is_quick_tap) return false;
          if (showQuickTaps && !subcategory.subcategory_is_quick_tap) return false;
          if (subcategory.subcategory_is_book) return false;
          // Check if this subcategory doesn't belong to any category and wasn't already added
          const isOrphaned = !subcategory.category_id || subcategory.category_id === 0;
          const notAlreadyAdded = !addedSubcategoryIds.has(String(subcategory.subcategory_id));
          return isOrphaned && notAlreadyAdded;
        })
        .forEach(subcategory => {
          let label = subcategory.subcategory_title;
          if (subcategory.subcategory_is_quick_tap) {
            label += ' (Quick Tap)';
          }
          if (!subcategory.subcategory_is_active) {
            label += ' (Inactive)';
          }

          options.push({
            label,
            value: String(subcategory.subcategory_id) + "SUB",
            group: "Orphaned Subcategories",
            disabled: !subcategory.subcategory_is_active,
            icon: CategoryOutlined
          });
        });
    }

    // Process audiobooks separately
    if (allowedTypes.includes('audiobook')) {
      all_subcategories
        .filter(subcategory => {
          if (!showQuickTaps && subcategory.subcategory_is_quick_tap ) return false;
          if (!subcategory.subcategory_is_book) return false;
          return true;
        })
        .forEach(subcategory => {
          let label = subcategory.subcategory_title;
          if (subcategory.subcategory_is_quick_tap) {
            label += ' (Quick Tap)';
          }

          options.push({
            label,
            value: String(subcategory.subcategory_id) + "AUD",
            group: "Audiobooks",
            icon: Book
          });
        });
    }

    // Add Challenges if allowed
    if (allowedTypes.includes('challenge')) {
      challenges.forEach(obj => {
        options.push({
          label: obj.challenge_title + " - Challenge",
          value: String(obj.challenge_id) + "CHA",
          group: "Challenges",
          icon: EmojiEvents
        });
      });
    }

    // Add Series if allowed
    if (allowedTypes.includes('series')) {
      series.forEach(obj => {
        options.push({
          label: obj.series_title + " - Series",
          value: String(obj.series_id) + "SER",
          group: "Series",
          icon: Collections
        });
      });
    }

    // Remove duplicates based on value (keeping the first occurrence)
    const uniqueOptions = [];
    const seenValues = new Set();
    const duplicates = [];

    for (const option of options) {
      if (!seenValues.has(option.value)) {
        seenValues.add(option.value);
        uniqueOptions.push(option);
      } else {
        duplicates.push(option);
      }
    }

    return uniqueOptions;
  }, [all_categories, all_subcategories, challenges, series, allowedTypes, showQuickTaps]);

  const findOptionByValue = (options, value) => {
    if (!value) return null;
    return options.find(option => option.value === value) || null;
  };

  return (
    <Autocomplete
      id="parent-selector"
      options={renderParentOptions}
      value={findOptionByValue(renderParentOptions, value)}
      onChange={(_, newValue) => onChange(newValue?.value || "0")}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      groupBy={(option) => option.group}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth={fullWidth}
          sx={{
            '& .MuiInputBase-input': {
              paddingTop: '3px !important',
              paddingBottom: '3px !important',
            }
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.value}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            opacity: option.disabled ? 0.6 : 1,
            fontStyle: option.disabled ? 'italic' : 'normal'
          }}>
            {option.icon && <option.icon sx={{ fontSize: '1rem', mr: 1 }} />}
            {option.label}
          </Box>
        </Box>
      )}
    />
  );
};

export default ParentSelector; 